import type { ApiKey } from "@repo/shared/types";
import { enGB, nb } from "date-fns/locale";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError } from "@/lib/api/_client";
import { useDeleteApiKey, useToggleApiKey } from "@/lib/hooks/use-api-keys";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { distanceToNowFromDb } from "@/lib/utils";
import { T } from "./api-keys-table.i18n";

const getDateLocale = (lang: string) => {
  return lang === "nb_NO" ? nb : enGB;
};

type ApiKeysTableProps = {
  apiKeys: ApiKey[];
};

export function ApiKeysTable({ apiKeys }: ApiKeysTableProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { confirm, ConfirmationDialog } = useConfirm();
  const deleteMutation = useDeleteApiKey();
  const toggleMutation = useToggleApiKey();

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deleteApiKey,
      description: t.deleteConfirmation(name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      const errorMsg = err instanceof ApiError ? err.message : t.failedToDeleteApiKey;
      await confirm({
        title: t.error,
        description: errorMsg,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleMutation.mutateAsync(id);
    } catch (err) {
      const errorMsg = err instanceof ApiError ? err.message : t.failedToToggleApiKey;
      await confirm({
        title: t.error,
        description: errorMsg,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  if (apiKeys.length === 0) {
    return <EmptyState>{t.noApiKeysYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.active}</TableHead>
            <TableHead>{t.lastUsed}</TableHead>
            <TableHead>{t.created}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apiKeys.map((apiKey) => (
            <TableRow key={apiKey.id}>
              <TableCell className="font-medium">{apiKey.name}</TableCell>
              <TableCell>
                <Switch
                  aria-label={`${t.active}: ${apiKey.name}`}
                  checked={apiKey.isActive}
                  onCheckedChange={() => handleToggle(apiKey.id)}
                  disabled={toggleMutation.isPending}
                />
              </TableCell>
              <TableCell>
                {apiKey.lastUsedAt
                  ? distanceToNowFromDb(apiKey.lastUsedAt, {
                      addSuffix: true,
                      locale: getDateLocale(userLanguage),
                    })
                  : t.never}
              </TableCell>
              <TableCell>
                {distanceToNowFromDb(apiKey.createdAt, {
                  addSuffix: true,
                  locale: getDateLocale(userLanguage),
                })}
              </TableCell>
              <TableActionsCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(apiKey.id, apiKey.name)}
                  disabled={deleteMutation.isPending}
                  tooltip={t.delete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog />
    </>
  );
}
